import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';

import { cx } from 'utils';
import { useAuth, useToggle, useClickOutside } from 'hooks';
import { PATHS } from 'routes/routePaths';
import { ROLE_LABEL } from 'config/roles';
import { UserIcon, SettingsIcon, LogoutIcon } from './icons';
import SettingsModal from './SettingsModal';
import ChangePasswordModal from './ChangePasswordModal';

/** Small lock glyph for the "Change password" action. */
const LockGlyph = (props) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" {...props}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2" stroke="currentColor" strokeWidth="1.75" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
);

/**
 * ProfileMenu — navbar identity affordance: an avatar button that opens a glass
 * dropdown with the signed-in user's details and account actions. Houses the
 * "logout" feature; Profile/Settings are placeholders until those pages exist.
 *
 * Animated with Framer Motion, dismissed via useClickOutside (outside-click or
 * Escape).
 */
const MENU = {
  initial: { opacity: 0, y: 8, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 8, scale: 0.96 },
  transition: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
};

export default function ProfileMenu() {
  const { user, profile, role, logout } = useAuth();
  const navigate = useNavigate();
  const [open, toggle, , close] = useToggle(false);
  const [settingsOpen, , openSettings, closeSettings] = useToggle(false);
  const [passwordOpen, , openPassword, closePassword] = useToggle(false);
  const ref = useClickOutside(close, open);

  const name = profile?.displayName || user?.email || 'Member';
  const email = user?.email || '';
  const initial = name.charAt(0).toUpperCase();

  const handleLogout = async () => {
    close();
    try {
      await logout();
      toast.success('Signed out.');
      navigate(PATHS.LOGIN, { replace: true });
    } catch {
      toast.error('Could not sign out. Please try again.');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-label="Account menu"
        aria-haspopup="true"
        aria-expanded={open}
        className={cx(
          'flex items-center gap-2 rounded-xl p-1 pr-1 transition-colors duration-200 ease-premium hover:bg-slate-100 sm:pr-2.5',
          open && 'bg-slate-100'
        )}
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-gradient text-sm font-semibold text-white shadow-soft">
          {initial}
        </span>
        <span className="hidden text-left leading-tight sm:block">
          <span className="block max-w-[10rem] truncate text-sm font-medium text-slate-900">{name}</span>
          <span className="block text-2xs font-medium text-brand-600">{ROLE_LABEL[role] || 'Member'}</span>
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            {...MENU}
            className="absolute right-0 mt-2 w-64 origin-top-right overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-elevated"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-gradient text-base font-semibold text-white">
                {initial}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-900">{name}</span>
                {email && <span className="block truncate text-xs text-slate-500">{email}</span>}
              </span>
            </div>

            <div className="py-1.5">
              <MenuItem icon={<UserIcon className="h-[18px] w-[18px]" />} label="Profile" />
              <MenuItem
                icon={<LockGlyph className="h-[18px] w-[18px]" />}
                label="Change password"
                onClick={() => {
                  close();
                  openPassword();
                }}
              />
              <MenuItem
                icon={<SettingsIcon className="h-[18px] w-[18px]" />}
                label="Settings"
                onClick={() => {
                  close();
                  openSettings();
                }}
              />
            </div>

            <div className="border-t border-slate-100 py-1.5">
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
              >
                <LogoutIcon className="h-[18px] w-[18px]" aria-hidden />
                Sign out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SettingsModal open={settingsOpen} onClose={closeSettings} />
      <ChangePasswordModal open={passwordOpen} onClose={closePassword} />
    </div>
  );
}

/** An account action (Profile / Settings) in the dropdown. */
function MenuItem({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
    >
      <span className="text-slate-400" aria-hidden>
        {icon}
      </span>
      {label}
    </button>
  );
}
